// Persisted Offline Setlist
// author rodolfo lopez pintor <rlp@nebular.tv>
//////////////////////////////////////////////////////////////
//noinspection DuplicatedCode

import m from "mithril";

import {
	defineWidget, Empty, CardieWidget, InputWidget,
	ButtonNew, Notification, confirm
} from "@nebularstreams/libmui"

import {
	startRemix, stopRemix, setlistList, playLiveIndex, removeLiveClip,
	setlistRemoveVideo, setlistRemove,
	setlistReorderVideo, isSetlistLive, insertVideo
} from "../../Api";

import {STATE_REMIX as STATE} from "../../State";

const

	TAG = "PersistedSetlist",
	//////////////////////////////////////////////////////////////
	// PERSISTED SETLISTS: STRINGS

	STR = {
		BITRATE: "Bitrate (kbps)",
		START_REMIX: "Start reMix",
		STOP_REMIX: "Stop reMix",
		STOP_REMIX_CONFIRM: "Stop the reMix? Your stream will not be live anymore, and your customers connections interrupted!",
		DELETE_SETLIST: "delete this reMix",
		DELETE_SETLIST_CONFIRM: "Delete this reMix ? This action cannot be undone!",
		REMOVE_VIDEO_PERSISTED: "remove this video from the reMix",
		REMOVE_VIDEO_CONFIRM: "Remove this clip from the reMix?",
		REMOVE_VIDEO_TITLE: "Remove this video",
		REMOVE_VIDEO_BUSY: "Cannot delete a video until the scheduled clip has started",
		REMOVE_VIDEO_REMIX: "Remove this video from the current reMix?",
		PLAY_VIDEO_BUSY: "Cannot play a video until the previous one has been acknowledged by the server",
		VIDEO_LOOPED: "[LOOPED]",
		SETLIST_EMPTY: "There are no videos in this reMix\nPlease add videos and start the session.",
		STATUS_PLAYING: "Playing",
		STATUS_STOPPED: "Stopped",
		STATUS_OFFLINE: "Offline",
		STATUS_VIDEOS: "videos",
		INVALID_DROP: "Invalid dropped item: Please drag items from your uploaded videos",
		ADDED_VIDEO: "Added video to the reMix"
	},

	///////////////////////////////////////////////////////////
	// COMMON BUTTONS: Start Remix, Stop Remix, Delete Setlist

	StartRemixButton = defineWidget(
		() => [
			m(InputWidget, {
				label: STR.BITRATE,
				value: STATE.bitrate,
				hint: "2500k",
				maxlength: 5,
				oninput: () => {
				}
			}),
			m(ButtonNew, {
				class: "start unitheight",
				icon: "power-off",
				text: STR.START_REMIX,
				onClick: () => startRemix(STATE.currentSetlist, STATE.bitrate, STATE.initialIndex)
					.then(() => {
						delete STATE.error;
						delete STATE.errorFatal;
					})
					.catch((e) => {
						STATE.error = e.message;
						STATE.errorFatal = true;
					})
					.finally(() => m.redraw())
			})
		]
	),

	StopRemixButton = defineWidget(
		() => m(ButtonNew, {
			class: "stop unitheight",
			icon: "power-off",
			text: "Stop Remix",
			onClick: () => {
				confirm(
					STR.STOP_REMIX_CONFIRM,
					() => stopRemix()
						.catch((e) => Notification.add(e.message))
				)
			}
		})
	),

	DeleteOfflineSetlistButton = defineWidget(
		({attrs}) => m(ButtonNew, {
			icon: "trash",
			text: STR.DELETE_SETLIST,
			class: "delete",
			onClick: () => confirm(
				STR.DELETE_SETLIST_CONFIRM,
				async () => {

					STATE.loadingSetlists = true;
					m.redraw();

					const result = await setlistRemove(attrs.setlist.bintuOrganization, attrs.setlist.setlistId);

					if (!result.success) {
						Notification.add(result.error);
						return;
					}

					delete STATE.currentSetlist;
					m.redraw();

					const list = await setlistList(attrs.setlist.bintuOrganization);

					if (list.success) {
						STATE.setlistList = list.data;
					} else {
						Notification.add(list.error);
					}
					STATE.loadingSetlists = false;
					m.redraw();
				})
		})
	),

	///////////////////////////////////////////////////////////
	// SELECTED SETLIST ITEM (NOT LIVE)

	// widget: The button at the right of a Persisted Setlist Item (not live)
	PersistedOfflineSetlistItemButton = defineWidget(
		({attrs}) => m(ButtonNew, {
			class: "unitwidth noshrink",
			icon: "trash",
			title: STR.REMOVE_VIDEO_PERSISTED,
			onClick: () => confirm(
				STR.REMOVE_VIDEO_CONFIRM,
				async () => {

					attrs.busy(true);
					m.redraw();

					const result = await setlistRemoveVideo(attrs.setlist.bintuOrganization, attrs.setlist.setlistId, attrs.item.videoId);

					if (result.success) {
						const index = attrs.setlist.videos.findIndex((item) => item.videoId === attrs.item.videoId);
						if (index >= 0) {
							attrs.setlist.videos.splice(index, 1);
						}
						m.redraw();
					} else {
						Notification.add(result.error);
					}

					attrs.busy(false);
				})
		}),
	),

	// widget: A non-live setlist Item
	PersistedOfflistSetlistItem = defineWidget(
		({attrs, state}) => (attrs.item.url && attrs.item.videoId)
			? [
				m("li",
					{
						"data-index": attrs.index,

						class: [
							(STATE.initialIndex === attrs.index) ? "current" : "",
							attrs.adding ? "adding" : ""
						].join(" "),

						draggable: true,
						ondragstart: (e) => {
							e.dataTransfer.effectAllowed = "copy";
							const payload = JSON.stringify({
								type: "videoItemMove",
								index: attrs.index
							});
							e.dataTransfer.setData("text/plain", payload);
							state.dragging = true;
							m.redraw();
						},
						ondragend: () => {
							state.dragging = false;
							m.redraw();
						},

					},
					[

						m(".flexrow", [
							m("span.index.unitwidth.noshrink", `#${attrs.index + 1}`),
							m("span.title.grows.ellipsis.ohidden", {
								onclick: () => {
									if (STATE.initialIndex === attrs.index) {
										delete STATE.initialIndex;
									} else {
										STATE.initialIndex = attrs.index;
									}
									m.redraw();
								}
							}, attrs.item.url.split("/").pop()),
							m("span.looped.unitwidth.noshrink", attrs.item.loop ? STR.VIDEO_LOOPED : ""),
							m(PersistedOfflineSetlistItemButton, attrs)
						])
					]
				),
			]
			: []
	),

	///////////////////////////////////////////////////////////
	// CURRENT LIVE SETLIST ITEM

	// widget: The changing button to the right of a Live Setlist Item
	PersistedLiveSetlistItemButton = defineWidget(
		({attrs}) => m(ButtonNew, {
			class: "unitwidth noshrink",
			icon: attrs.current ? "play" : STATE.cued === attrs.index ? "sync.fa-spin.black" : "trash",
			title: attrs.current ? "" : STR.REMOVE_VIDEO_TITLE,
			onClick: () => {

				// Do not allow to delete the currently playing video
				if (!attrs.current) {

					if (STATE.cued) {
						Notification.add(STR.REMOVE_VIDEO_BUSY);
						return;
					}

					confirm(
						STR.REMOVE_VIDEO_REMIX,
						() => removeLiveClip(attrs.index)
							.then(() => m.redraw())
							.catch((e) => Notification.add(e.message))
					);
				}

			}
		}),
	),

	// widget: A live setlist Item
	PersistedLiveSetlistItem = defineWidget(
		({attrs}) => (attrs.item.url && attrs.item.videoId)
			? m("li",
				{
					class: [
						(attrs.current
								? "current"
								: STATE.cued === attrs.index
									? "cued"
									: ""
						),
						(attrs.item.deleted ? "deleted" : ""),
						attrs.adding ? "adding" : ""
					].join(" "),
					"data-index": attrs.index
				},

				m(".flexrow", [

					// video number
					m("span.index.unitwidth.noshrink", `#${attrs.index + 1}`),

					// video title - clickable to play video
					m("span.title.grows.ellipsis.ohidden",
						{
							onclick: () => {
								if (STATE.cued) {
									Notification.add(STR.PLAY_VIDEO_BUSY);
									return;
								}
								playLiveIndex(attrs.index)
									.then(() => m.redraw())
									.catch((e) => {
										delete STATE.cued;
										Notification.add(e.message);
										m.redraw();
									});
							}
						},
						attrs.item.url.split("/").pop()
					),

					// video flags
					m("span.looped.unitwidth.noshrink", attrs.item.loop ? STR.VIDEO_LOOPED : ""),

					// video right button
					m(PersistedLiveSetlistItemButton, attrs)

				])
			)
			: []
	),

	///////////////////////////////////////////////////////////
	// MAIN LIST WIDGET



	// widget: Playlist
	PersistedSetlist = defineWidget(
		(vnode) => {

			const
				{setlist, current, status} = vnode.attrs,
				playing = status?.playing;

			vnode.state.setlist = setlist;

			return m(CardieWidget,
				{
					class: "setlist" + (current ? " islive" : " notlive") + (vnode.state.dragging ? " dragging" : ""),
					element: "div",
					title: current && status
						? `${setlist.title} (${setlist.videos.length} ${STR.STATUS_VIDEOS}) - [${status.playing ? STR.STATUS_PLAYING : STR.STATUS_STOPPED}]`
						: `${setlist.title} (${setlist.videos.length} ${STR.STATUS_VIDEOS}) - [${STR.STATUS_OFFLINE}]`,
					bodyClass: "scrolly",
					cols: true,
					// bottom options: current: decide
					alwaysView: () => m(
						".flexrow.footer.wrap",
						current
							? playing
								? [m(StopRemixButton)]
								: [
									...setlist?.videos.length ? [m(StartRemixButton)] : [],
									m(DeleteOfflineSetlistButton, {setlist})
								]
							: [
								...setlist?.videos.length ? [m(StartRemixButton)] : [],
								m(DeleteOfflineSetlistButton, {setlist})
							])
				},
				setlist.videos.length
					? m(
						"ul.flexcol.grows.w-100",
						setlist.videos.map(
							(item, index) => m(
								current && playing
									? PersistedLiveSetlistItem
									: PersistedOfflistSetlistItem,
								{
									item,
									index,
									setlist,
									current: current && status?.playing && status.current === index,
									adding: vnode.state.dragindex === index,
									busy: (status) => {
										if (status) {
											// do not dereference DOM !
											vnode.dom.classList.add("busy");
										} else {
											// do not dereference DOM !
											vnode.dom.classList.remove("busy");
										}
									}
								})
						))
					: m(Empty, {
						icon: "video-slash",
						message: STR.SETLIST_EMPTY
					})
			)
		},
		undefined,
		(vnode) => {

			const setDragging = (enable) => {
				vnode.state.dragging = !!enable;
				if (!enable) {
					delete vnode.state.dragindex;
				}
				m.redraw();
			};
			// setup drag handlers after dom has been inited
			Object.assign(vnode.dom, {

				ondragover: (e) => {
					e.preventDefault();
					e.dataTransfer.dropEffect = "copy";
					setDragging(true);

					const
						target = e.target.hasAttribute("data-index")
							? e.target
							: e.target.parentNode.hasAttribute("data-index")
								? e.target.parentNode
								: e.target.parentNode.parentNode.hasAttribute("data-index")
									? e.target.parentNode.parentNode
									: undefined;

					const
						index = target ? parseInt(target.getAttribute("data-index")) : undefined;

					if (vnode.state.dragindex !== index) {
						vnode.state.dragindex = index;
						m.redraw();
					}

					console.log(TAG, "DragOver", index, e);

				},

				ondragleave: () => setDragging(false),

				ondrop: async (e) => {

					console.log(TAG, "Drop", e);

					// add video to the setlist

					e.preventDefault();

					const
						{setlist} = vnode.state,
						{videos} = setlist,
						index = vnode.state.dragindex;

					let newOrder = (videos.length + 1) * 10000;

					if (index >= 0) {

						// insert before item index
						console.log(TAG, "Insert before item", index);

						const
							beforeItem = videos[index],
							previous = videos[index - 1];

						newOrder = previous
							? (previous.order + beforeItem.order) / 2
							: beforeItem.order / 2
					}

					setDragging(false);

					let result;

					const data = e.dataTransfer.getData("text/plain");

					if (!data) {
						Notification.add(STR.INVALID_DROP);
						return;
					}

					const
						{type, url, index: originalIndex} = JSON.parse(data),
						isLive = isSetlistLive(setlist.setlistId);

					if (type === "videoItemMove") {
						console.log(TAG, "Reorder item original", originalIndex);
						const originalVideo = videos[originalIndex];
						try {
							vnode.dom.classList.add("busy");
							result = await setlistReorderVideo(
								setlist.bintuOrganization,
								setlist.setlistId,
								originalVideo.videoId,
								newOrder
							);
						} catch (e) {
							result = {error: e.message};
						} finally {
							vnode.dom.classList.remove("busy");

							console.log(TAG, result);

							if (result.success) {

								if (!isLive) {

									originalVideo.order = newOrder;
									setlist.videos = videos.sort((a, b) => a.order < b.order ? -1 : 1)
									m.redraw();
								}

							} else {
								Notification.add(result.error);
							}
						}

					} else if (type === "videoItem" && url) {

						vnode.dom.classList.add("busy");
						try {
							await insertVideo(vnode.state.setlist, url, index, newOrder);
							Notification.add(`${STR.ADDED_VIDEO}: ${url.split("/").pop()}`);
						} catch (e) {
							Notification.add(e.message);
						} finally {
							vnode.dom.classList.remove("busy");
							m.redraw();
						}


					} else {
						Notification.add(STR.INVALID_DROP);
					}
				}
			});
		});

export default PersistedSetlist;
